import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from 'lucide-react';
import { api } from '../hooks/api.js';
import { microCreditsToCredits } from '@blogforge/shared';

// ─────────────────────────────────────────────────────────────────
// Paystack redirects the customer back here after checkout
// (/billing/return?reference=<bf_…>). We verify + settle through
// POST /api/billing/purchases/verify — the same idempotent settle path
// the charge.success webhook uses, so credits can never double-land.
// ─────────────────────────────────────────────────────────────────

export function BillingReturnPage() {
  const [searchParams] = useSearchParams();
  const reference = searchParams.get('reference') ?? '';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [started, setStarted] = useState(false);

  const verifyMutation = useMutation({
    mutationFn: () => api.billing.purchases.verify(reference),
    onSuccess: () => {
      // Balance + ledger changed — refresh everything billing-keyed.
      void qc.invalidateQueries({ queryKey: ['billing'] });
    },
  });

  useEffect(() => {
    if (reference && !started) {
      setStarted(true);
      verifyMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference, started]);

  const outcome = verifyMutation.data;

  let body: React.ReactNode;
  if (!reference) {
    body = (
      <EmptyState
        tone="danger"
        icon={<XCircle size={26} strokeWidth={1.8} />}
        title="Missing payment reference"
        message="No reference was provided in the callback URL."
      />
    );
  } else if (verifyMutation.isPending) {
    body = (
      <div className="flex flex-col items-center gap-4 py-10 text-center">
        <Loader2 size={30} className="animate-spin text-accent" />
        <div>
          <h2 className="text-base font-semibold text-ink">Verifying your payment…</h2>
          <p className="mt-1 max-w-sm text-[13px] text-muted">
            Checking the transaction with Paystack and crediting your workspace. This usually takes
            a moment.
          </p>
        </div>
      </div>
    );
  } else if (verifyMutation.isError) {
    body = (
      <EmptyState
        tone="danger"
        icon={<XCircle size={26} strokeWidth={1.8} />}
        title="We couldn't verify that payment"
        message={
          verifyMutation.error instanceof Error
            ? verifyMutation.error.message
            : 'Something went wrong while verifying the payment.'
        }
      />
    );
  } else if (outcome?.status === 'paid' || outcome?.status === 'already_paid') {
    const added =
      typeof outcome.creditsAddedMicro === 'number'
        ? microCreditsToCredits(outcome.creditsAddedMicro)
        : null;
    body = (
      <EmptyState
        tone="success"
        icon={<CheckCircle2 size={26} strokeWidth={1.8} />}
        title={outcome.status === 'already_paid' ? 'Payment already applied' : 'Payment confirmed'}
        message={
          added !== null
            ? `Your workspace was credited with ${added.toLocaleString(undefined, { maximumFractionDigits: 2 })} credits.`
            : 'Your subscription is active — check the Plan tab for the new period.'
        }
      />
    );
  } else if (outcome?.status === 'pending') {
    body = (
      <EmptyState
        tone="neutral"
        title="Payment still processing"
        message="Paystack hasn't confirmed the transaction yet. Once it succeeds, the webhook will credit your workspace automatically — check back shortly."
      />
    );
  } else {
    body = (
      <EmptyState
        tone="danger"
        icon={<XCircle size={26} strokeWidth={1.8} />}
        title="Payment was not completed"
        message="The transaction was not successful, so no credits were added. You can try again from the billing page."
      />
    );
  }

  return (
    <div className="flex h-full items-center justify-center overflow-y-auto px-8 py-10">
      <div className="w-full max-w-md">
        <div className="surface-row flex flex-col items-center px-6 py-8 text-center">
          {body}
          <button
            type="button"
            onClick={() => navigate('/billing')}
            className="btn-ghost mt-6 px-4 py-2 text-[13px]"
          >
            <ArrowLeft size={14} />
            Back to Billing
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({
  tone,
  icon,
  title,
  message,
}: {
  tone: 'success' | 'danger' | 'neutral';
  icon?: React.ReactNode;
  title: string;
  message: string;
}) {
  const toneClasses =
    tone === 'success'
      ? 'bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]'
      : tone === 'danger'
        ? 'bg-danger/10 text-danger'
        : 'bg-bg text-muted';
  return (
    <div className="flex flex-col items-center">
      <div className={`mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ${toneClasses}`}>
        {icon}
      </div>
      <h2 className="text-base font-semibold text-ink">{title}</h2>
      <p className="mt-1.5 max-w-sm text-[13px] text-muted">{message}</p>
    </div>
  );
}
